import _ from 'lodash';
import * as mobx from 'mobx';
import { DataTablePresenter, ITableColumn } from './data-table.presenter';
import { IFilterSchema, ICustomFilter } from './components';

export class InfiniteScrollDataTablePresenter<D extends object> extends DataTablePresenter<D> {
    @mobx.observable.shallow public accumulatedData: D[] = [];
    @mobx.observable.ref public currentPage: number = 0;
    @mobx.observable.ref public hasMore: boolean = true;
    @mobx.observable.ref public isLoadingMore: boolean = false;

    private loadedIds: Set<string> = new Set();

    constructor(config: {
        readonly name: string;
        data?: D[];
        columns: ITableColumn<D>[];
        filterSchemas: IFilterSchema[];
        selectedIds?: Record<string, boolean>;
        filters?: ICustomFilter[];
        onSelected?: (selectedIds: Record<string, boolean>) => void;
        filterParser?: (value: string) => ICustomFilter[][];
    }) {
        const data = config.data || [];
        super({
            ...config,
            data,
        });
        this.accumulatedData = data;
        this.updateLoadedIds();
    }

    @mobx.action
    public appendData = (items: D[]): void => {
        const newItems = items.filter(item => {
            const id = this.getItemId(item);
            if (this.loadedIds.has(id)) {
                return false;
            }
            this.loadedIds.add(id);
            return true;
        });

        if (newItems.length > 0) {
            this.accumulatedData = [...this.accumulatedData, ...newItems];
            this.data = this.accumulatedData;
            this.dataTotal = this.accumulatedData.length;
        }
    };

    @mobx.action
    public resetData = (): void => {
        this.accumulatedData = [];
        this.currentPage = 0;
        this.hasMore = true;
        this.isLoadingMore = false;
        this.loadedIds.clear();
        this.data = [];
        this.dataTotal = 0;
    };

    @mobx.action
    public setHasMore = (hasMore: boolean): void => {
        this.hasMore = hasMore;
    };

    @mobx.action
    public setCurrentPage = (page: number): void => {
        this.currentPage = page;
    };

    @mobx.action
    public setIsLoadingMore = (isLoading: boolean): void => {
        this.isLoadingMore = isLoading;
    };

    @mobx.action
    public incrementPage = (): void => {
        this.currentPage += 1;
    };

    @mobx.action
    public updateItem = (updatedItem: D): void => {
        const id = this.getItemId(updatedItem);
        const index = this.accumulatedData.findIndex(item => this.getItemId(item) === id);
        if (index !== -1) {
            this.accumulatedData = [
                ...this.accumulatedData.slice(0, index),
                updatedItem,
                ...this.accumulatedData.slice(index + 1),
            ];
            this.data = this.accumulatedData;
        }
    };

    @mobx.override
    public onDataUpdate(data: D[]): void {
        if (this.accumulatedData.length === 0) {
            this.accumulatedData = data;
            this.updateLoadedIds();
            this.data = this.accumulatedData;
            this.dataTotal = this.accumulatedData.length;
        } else {
            this.appendData(data);
        }
    }

    private getItemId = (item: D): string => {
        // Try to get id from item, fallback to stringifying the entire object
        // Note: For best performance, items should have an 'id' property
        return (item as any).id || JSON.stringify(item);
    };

    private updateLoadedIds = (): void => {
        this.loadedIds.clear();
        this.accumulatedData.forEach(item => {
            const id = this.getItemId(item);
            this.loadedIds.add(id);
        });
    };
}

